import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MapPin, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import usePageTitle from "@/lib/usePageTitle";

const Contact = () => {
  usePageTitle("Contact");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message was sent to the township board.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-le section-pad grid grid-cols-1 md:grid-cols-5 gap-12" data-testid="contact-page">
      <div className="md:col-span-2">
        <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-3">Get in touch</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Contact Us</h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          Questions, suggestions, or concerns? Send us a message or use the live chat in the bottom-right corner.
        </p>

        <div className="mt-10 space-y-5">
          <div className="flex items-start gap-3">
            <MapPin className="text-accent mt-1" size={18} />
            <div>
              <div className="font-medium">Lake Eunice Township</div>
              <div className="text-sm text-muted-foreground">12708 Townhall Rd<br />Audubon, MN 56511</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="text-accent mt-1" size={18} />
            <div>
              <div className="font-medium">Email</div>
              <a className="text-sm text-secondary link-underline" href="mailto:Lakeeunicetwp@gmail.com">Lakeeunicetwp@gmail.com</a>
            </div>
          </div>
        </div>

        <iframe
          title="Lake Eunice Town Hall map"
          src="https://maps.google.com/maps?q=12708%20Townhall%20Rd%20Audubon%20MN&t=&z=12&ie=UTF8&iwloc=&output=embed"
          className="w-full h-56 mt-8 rounded-xl border border-border"
        />
      </div>

      <Card className="md:col-span-3 border-border" data-testid="contact-form-card">
        <CardContent className="p-7 md:p-10">
          <h2 className="font-heading text-2xl font-semibold">Feedback Form</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We would love to hear your thoughts, suggestions, concerns or problems so we can improve.
          </p>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7">
            <div>
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" value={form.name} onChange={onChange("name")} data-testid="contact-name-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={form.email} onChange={onChange("email")} data-testid="contact-email-input" className="mt-1.5" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="c-subject">Subject</Label>
              <Input id="c-subject" value={form.subject} onChange={onChange("subject")} data-testid="contact-subject-input" className="mt-1.5" />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="c-message">Message</Label>
              <Textarea id="c-message" rows={6} value={form.message} onChange={onChange("message")} data-testid="contact-message-input" className="mt-1.5" />
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={sending} className="bg-primary hover:bg-primary/90" data-testid="contact-submit-btn">
                {sending ? "Sending..." : "Send message"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Contact;
